import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskActions } from '../../../../hooks/useTaskActions';
import { useGeoMarkupStore } from '../../../../store';
import { useNotifications } from '../../../../hooks/useNotifications';
import { useLocalizedDate } from '../../../../hooks/useLocalizedDate';
import { TaskStatus, Task, RelatedTaskSummary } from '../../../../types';
import { generateId } from '../../../../utils/helpers';
import { InlineRequirement } from '../types';
import type {
  UseTaskWizardFormReturn,
  MantineWizardFormData,
} from '../utils/mantineFormSchema';
import { DEFAULT_MAP_CENTER } from '../../../../constants';
import { areGeometryArraysEqual } from '../../../../utils/geometryEditHelpers';
import { useWizardErrorHandler } from '../utils/errorHandling';
import { attachmentsService } from '../../../../services/api/attachmentsService';

// Find first step with validation errors
function findFirstStepWithErrors(
  formErrors: Partial<Record<keyof MantineWizardFormData, string>>
): number | null {
  // Step 1 fields
  const step1Fields: (keyof MantineWizardFormData)[] = [
    'title',
    'description',
    'assignedToId',
    'priority',
    'startDate',
    'dueDate',
    'location',
    'locationFromMap',
  ];
  if (step1Fields.some((field) => formErrors[field])) return 1;

  // Step 2 fields
  const step2Fields: (keyof MantineWizardFormData)[] = ['attachments'];
  if (step2Fields.some((field) => formErrors[field])) return 2;

  // Step 3 fields
  if (formErrors.requirements) return 3;

  // Step 4 fields
  const step4Fields: (keyof MantineWizardFormData)[] = [
    'relatedTaskIds',
    'requireRelatedTasksCompletion',
  ];
  if (step4Fields.some((field) => formErrors[field])) return 4;

  return null;
}

async function uploadAttachments(taskId: string, files: File[]): Promise<void> {
  if (files.length > 0) {
    await Promise.all(
      files.map((file) => attachmentsService.upload(taskId, file))
    );
  }
}

function createRelatedTaskStub(taskId: string): RelatedTaskSummary {
  return {
    id: taskId,
    shortId: '',
    title: '',
    status: TaskStatus.Pending,
    location: null,
    dueDate: null,
  };
}

interface UseTaskSubmissionProps {
  form: UseTaskWizardFormReturn['form'];
  editingTask?: Task | null;
  forceClose: () => void;
  goToStep: (step: number) => boolean;
}

export const useTaskSubmission = ({
  form,
  editingTask,
  forceClose,
  goToStep,
}: UseTaskSubmissionProps) => {
  const { t } = useTranslation();
  const handleError = useWizardErrorHandler('useTaskSubmission');
  const { showError, showWarning } = useNotifications();
  const { parseDate } = useLocalizedDate();
  const {
    createTaskApi,
    updateTaskApi,
    fetchTaskById,
    addRequirement,
    updateRequirement,
    deleteRequirement,
    selectTask,
  } = useTaskActions();
  const storeAnchorPoint = useGeoMarkupStore(
    (state) => state.tasks.formAnchorPoint
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate edit session ID for existing task edits (for batching history)
  const editSessionId = editingTask ? generateId() : null;

  // Synchronize requirements between old and new state
  const syncRequirements = useCallback(
    (
      taskId: string,
      oldRequirements: Task['requirements'],
      newRequirements: InlineRequirement[],
      batchId?: string
    ) => {
      // Find added requirements (those without id or with tempId that don't exist in old)
      const added = newRequirements.filter(
        (newReq) =>
          !newReq.id ||
          !oldRequirements.find((oldReq) => oldReq.id === newReq.id)
      );

      // Find deleted requirements (those in old but not in new)
      const deleted = oldRequirements.filter(
        (oldReq) => !newRequirements.find((newReq) => newReq.id === oldReq.id)
      );

      // Find updated requirements (those with same id but different content)
      const updated = newRequirements.filter((newReq) => {
        if (!newReq.id) return false; // Skip new requirements
        const oldReq = oldRequirements.find(
          (oldReq) => oldReq.id === newReq.id
        );
        if (!oldReq) return false;

        return (
          oldReq.text !== newReq.text ||
          oldReq.completed !== newReq.completed ||
          oldReq.order !== newReq.order
        );
      });

      // Dispatch changes with batchId
      added.forEach((req: InlineRequirement) => {
        const reqData = {
          text: req.text,
          completed: req.completed,
          order: req.order,
        };
        addRequirement(taskId, reqData, batchId);
      });

      deleted.forEach((req: Task['requirements'][0]) => {
        deleteRequirement(taskId, req.id, batchId);
      });

      updated.forEach((req: InlineRequirement) => {
        if (req.id) {
          const updates = {
            text: req.text,
            completed: req.completed,
            order: req.order,
          };
          updateRequirement(taskId, req.id, updates, batchId);
        }
      });
    },
    [addRequirement, updateRequirement, deleteRequirement]
  );

  const handleSubmit = useCallback(async () => {
    const validation = form.validate();

    // Handle validation errors with navigation
    if (validation.hasErrors) {
      const firstErrorStep = findFirstStepWithErrors(validation.errors);
      if (firstErrorStep) {
        goToStep(firstErrorStep);
        showWarning(t('validation.fixErrorsInStep', { step: firstErrorStep }));
      }
      return; // Stop submission
    }

    // No errors - proceed with submission
    setIsSubmitting(true);
    try {
      // Map form values to Task interface
      const now = new Date();

      // Get geometries from Zustand store (managed by MapToolbox)
      const currentGeometries = useGeoMarkupStore.getState().currentGeometries;

      // Filter out placeholder geometries (runtime UI artifacts only)
      // Placeholders are created for settings UI before first map click
      const geometries = currentGeometries.filter(
        (geometry) => !geometry.isPlaceholder
      );

      // VALIDATION: After filtering, require at least one real geometry
      // Placeholders don't count as valid geometries for task creation
      if (geometries.length === 0) {
        // Exit fullscreen so the map (and drawing tools) becomes visible —
        // user otherwise has no way to add geometry from a map-covering panel.
        useGeoMarkupStore.getState().ui.setSlidePanelFullscreen(false);
        showError(t('validation.geometryRequired'));
        setIsSubmitting(false);
        return;
      }

      // Get anchor point from Zustand with fallback to default center
      const anchorPoint = storeAnchorPoint || DEFAULT_MAP_CENTER;

      const taskData = {
        title: form.values.title.trim(),
        description: form.values.description.trim(),
        assignedToId: form.values.assignedToId || null,
        assignedToName: null, // Resolved by backend from assignedToId
        assignedToIsFormerMember: false,
        priority: form.values.priority,
        geometries, // Get from Zustand, not form
        anchorPoint,
        location: form.values.location?.trim() || undefined,
        // requirements: handled separately via individual actions
        attachments: [],
        startDate: form.values.startDate
          ? parseDate(form.values.startDate)
          : null,
        dueDate: form.values.dueDate ? parseDate(form.values.dueDate) : null,
      };

      if (editingTask) {
        // Update existing task via API
        const batchId = editSessionId || undefined;

        // Check if geometries have actually changed (deep comparison)
        const geometriesChanged = !areGeometryArraysEqual(
          editingTask.geometries,
          geometries
        );

        // Only include geometries in update if they actually changed
        // This prevents unnecessary geometry refreshes and unwanted history events
        const updatedTaskData = {
          title: taskData.title,
          description: taskData.description,
          assignedToId: taskData.assignedToId,
          priority: taskData.priority,
          anchorPoint: taskData.anchorPoint,
          location: taskData.location,
          startDate: taskData.startDate,
          dueDate: taskData.dueDate,
          relatedTasks: form.values.relatedTaskIds.map(createRelatedTaskStub),
          requireRelatedTasksCompletion:
            form.values.requireRelatedTasksCompletion,
          ...(geometriesChanged ? { geometries } : {}),
        };

        // Call API and handle response
        const success = await updateTaskApi(editingTask.id, updatedTaskData);
        if (!success) {
          showError(t('notifications.taskUpdateFailed'));
          setIsSubmitting(false);
          return; // Don't close form - allow retry
        }

        // Upload new attachments after task update
        if (form.values.attachments.length > 0) {
          await uploadAttachments(editingTask.id, form.values.attachments);
        }
        // Always refetch task to sync store with latest server state
        await fetchTaskById(editingTask.id);

        syncRequirements(
          editingTask.id,
          editingTask.requirements,
          form.values.requirements,
          batchId
        );
      } else {
        // Create new task via API
        const newTaskData = {
          ...taskData,
          shortId: '',
          completedAt: null,
          status: TaskStatus.InProgress,
          comments: [],
          history: [],
          requirements: form.values.requirements.map((requirement) => ({
            ...requirement,
            createdAt: now,
          })),
          relatedTasks: form.values.relatedTaskIds.map(createRelatedTaskStub),
          requireRelatedTasksCompletion:
            form.values.requireRelatedTasksCompletion,
        };

        const newTaskId = await createTaskApi(newTaskData);
        if (!newTaskId) {
          showError(t('notifications.taskCreateFailed'));
          setIsSubmitting(false);
          return; // Don't close form - allow retry
        }

        // Upload new attachments after task creation
        if (form.values.attachments.length > 0) {
          await uploadAttachments(newTaskId, form.values.attachments);
          // Refetch task to update store with new attachments
          await fetchTaskById(newTaskId);
        }

        // Auto-select newly created task to open details panel after form closes
        selectTask(newTaskId);
      }

      forceClose();
    } catch (error) {
      const errorMsg = editingTask
        ? 'Failed to update task'
        : 'Failed to create task';
      handleError(
        error,
        'task submission',
        { isEdit: !!editingTask },
        errorMsg
      );
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    editingTask,
    editSessionId,
    updateTaskApi,
    syncRequirements,
    createTaskApi,
    forceClose,
    showError,
    showWarning,
    goToStep,
    t,
    parseDate,
    storeAnchorPoint,
    selectTask,
  ]);

  return {
    isSubmitting,
    editSessionId,
    handleSubmit,
  };
};
